import { useMemo, useState } from "react";

// 3.A.4 (vaihe 3.A, ADR-013):
// Per-source-root konvention input + autocreate-checkbox. Käyttäjä syöttää
// konvention kuten "/Users/kimmos/Movies/{project}/" jolla daemon
// auto-tunnistaa projekti-kansiot.
//
// Refactor: näkyvillä vain jos konventio on asetettu. Poista-nappi tyhjentää
// konvention (käytännössä poistaa rivin listalta levyt.tsx:n filterissä).
//
// v1 tukee vain {project}-placeholderia. {date:...}-tokenit varattu
// tulevaisuuteen (vaihe 3.B+ kun media-import-flow kysyy päivämääriä).

type ValidationError =
  | { kind: "ok" }
  | { kind: "needs-slash" }
  | { kind: "needs-placeholder" }
  | { kind: "multiple-placeholders" }
  | { kind: "wrong-root"; sourceRoot: string };

function validatePattern(pattern: string, sourceRoot: string): ValidationError {
  const trimmed = pattern.trim();
  if (trimmed.length === 0) return { kind: "ok" }; // tyhjä = poista (sallittu)
  if (!trimmed.startsWith("/")) return { kind: "needs-slash" };
  const placeholderCount = (trimmed.match(/\{project\}/g) ?? []).length;
  if (placeholderCount === 0) return { kind: "needs-placeholder" };
  if (placeholderCount > 1) return { kind: "multiple-placeholders" };
  const rootNormalized = sourceRoot.replace(/\/+$/, "");
  if (
    !trimmed.startsWith(rootNormalized + "/") &&
    trimmed !== rootNormalized + "/{project}"
  ) {
    return { kind: "wrong-root", sourceRoot: rootNormalized };
  }
  return { kind: "ok" };
}

function errorMessage(err: ValidationError): string | null {
  switch (err.kind) {
    case "ok":
      return null;
    case "needs-slash":
      return "Pattern alkaa /-merkillä (absoluuttinen polku).";
    case "needs-placeholder":
      return "Pattern tarvitsee {project}-placeholderin — paikan josta projekti-nimi luetaan.";
    case "multiple-placeholders":
      return "Vain yksi {project}-placeholder sallittu per pattern.";
    case "wrong-root":
      return `Pattern pitää olla source-rootin ${err.sourceRoot} alla.`;
  }
}

export type SourceRootConventionEditorProps = {
  sourceRoot: string;
  /** Nykyinen konventio (templaatti). */
  convention: string;
  autoCreate: boolean;
  /** Tallennus PUT /daemon/machines/:id/config:lla. */
  onChange: (convention: string, autoCreate: boolean) => Promise<void> | void;
  /** Poista konventio kokonaan (tallentaa tyhjän stringin → filteröityy listalta). */
  onRemove: () => Promise<void> | void;
  disabled?: boolean;
};

export function SourceRootConventionEditor({
  sourceRoot,
  convention,
  autoCreate,
  onChange,
  onRemove,
  disabled,
}: SourceRootConventionEditorProps) {
  const [draft, setDraft] = useState(convention);
  const [draftAutoCreate, setDraftAutoCreate] = useState(autoCreate);
  const [busy, setBusy] = useState(false);

  const dirty = draft !== convention || draftAutoCreate !== autoCreate;
  const validation = useMemo(() => validatePattern(draft, sourceRoot), [draft, sourceRoot]);
  const validationMsg = errorMessage(validation);
  const canSave = dirty && validation.kind === "ok";

  async function handleSave() {
    setBusy(true);
    try {
      await onChange(draft, draftAutoCreate);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!confirm(`Poista konventio source-rootilta ${sourceRoot}?`)) return;
    setBusy(true);
    try {
      await onRemove();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 rounded border border-slate-700 bg-slate-900 p-3">
      <div className="flex items-start justify-between gap-2">
        <code className="block min-w-0 flex-1 truncate text-xs text-slate-400">
          {sourceRoot}
        </code>
        <button
          type="button"
          onClick={() => void handleRemove()}
          disabled={disabled || busy}
          className="rounded bg-red-900/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-200 hover:bg-red-900/60 disabled:opacity-50"
          title="Poista konventio tältä source-rootilta"
        >
          Poista
        </button>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`${sourceRoot}/{project}/`}
          disabled={disabled || busy}
          aria-invalid={validation.kind !== "ok"}
          className={`flex-1 rounded bg-slate-800 px-2 py-1 text-xs text-slate-100 placeholder-slate-500 disabled:opacity-50 ${
            validation.kind === "ok" ? "" : "border border-red-700"
          }`}
        />
      </div>
      {validationMsg && (
        <p className="text-xs text-red-300">{validationMsg}</p>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <input
          type="checkbox"
          checked={draftAutoCreate}
          onChange={(e) => setDraftAutoCreate(e.target.checked)}
          disabled={disabled || busy}
        />
        Luo automaattisesti `.kratti-project.yaml` kun konventio matchaa uutta
        kansiota
      </label>
      <p className="text-xs text-slate-500">
        Käytä <code className="rounded bg-slate-800 px-1">{`{project}`}</code>-
        placeholderia kohdassa josta projekti-nimi luetaan. Esim.{" "}
        <code className="rounded bg-slate-800 px-1">
          {sourceRoot.replace(/\/+$/, "")}/{`{project}`}/
        </code>{" "}
        tarkoittaa että jokainen suora alikansio on oma projekti.
      </p>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={disabled || busy || !canSave}
          className="rounded bg-cyan-700 px-3 py-1 text-xs font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
          title={
            validation.kind !== "ok" && dirty
              ? "Korjaa pattern-validointivirhe ennen tallennusta"
              : undefined
          }
        >
          {busy ? "Tallennetaan..." : "Tallenna"}
        </button>
      </div>
    </div>
  );
}

// Eksportoitu testattavuutta varten
export { validatePattern, errorMessage };
export type { ValidationError };
